import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { ColumnType } from "antd/es/table/interface";
import { App, Checkbox } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  UndoOutlined,
} from "@ant-design/icons";

import QueryTable from "@/components_v2/Tables/QueryTable";
import {
  useLazyGetAdminSurveyStatisticsQuery,
  useRevertSurveyAssignmentSubmissionMutation,
  useUpdateSurveyAssignmentMutation,
} from "@/redux/service.js";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.js";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import { useSurveyViewDownload } from "@/domains/survey-views/hooks/useSurveyViewDownload";
import { SurveyStatus } from "@/shared/utils/constants";
import {
  SUBMISSION_TIMING,
  SurveyStatisticsFiltersState,
  SurveyStatisticsTableProps,
} from "../types";
import { SurveyAssignmentStatisticsAdminResponse } from "@/redux/survey/types";
import SurveyStatisticsFilters from "@/features/survey/management/components/SurveyStatisticsFilters";

const RevertSubmissionActionsCell = ({
  assignment,
  surveyStatus,
}: {
  assignment: SurveyAssignmentStatisticsAdminResponse;
  surveyStatus?: string;
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [revertSubmission, { isLoading }] =
    useRevertSurveyAssignmentSubmissionMutation();

  const disabledReason = !assignment.isSubmitted
    ? t("survey.errors.report_not_submitted")
    : surveyStatus === SurveyStatus.ENDED.value
      ? t("survey.errors.survey_ended")
      : "";

  const handleRevertSubmission = () => {
    revertSubmission(assignment.id)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("survey.labels.submissionReverted"),
        });
        setOpenConfirmModal(false);
      })
      .catch((errors) => {
        const code = errors?.data?.errors?.[0]?.code;
        notification.error({
          message: t(`survey.errors.${code}`, {
            defaultValue: t("survey.errors.default"),
          }),
        });
      });
  };

  return (
    <>
      <DropdownMenu
        content={
          <div>
            <BaseTooltip title={disabledReason}>
              <BaseButton
                className="dropdown-menu-button"
                isFullWidth
                variant="link"
                size="small"
                disabled={Boolean(disabledReason) || isLoading}
                leadingIcon={<UndoOutlined />}
                onClick={() => setOpenConfirmModal(true)}
              >
                {t("survey.labels.revertSubmission")}
              </BaseButton>
            </BaseTooltip>
          </div>
        }
        placement="bottom"
      >
        <BaseButton
          variant="link"
          size="small"
          leadingIcon={<EllipsisOutlined />}
        />
      </DropdownMenu>

      <CancelConfirmationModal
        open={openConfirmModal}
        onCancel={() => setOpenConfirmModal(false)}
        onConfirm={handleRevertSubmission}
        title={t("survey.labels.confirmRevertSubmission")}
        confirmButtonText={t("survey.labels.revertSubmission")}
        content={t("survey.labels.confirmRevertSubmissionContent", {
          name:
            assignment.organization?.name || assignment.user?.fullName || "",
        })}
      />
    </>
  );
};

const SurveyStatisticsTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  filtersCallback,
  softDeadline,
  surveyStatus,
  ...props
}: SurveyStatisticsTableProps<SurveyAssignmentStatisticsAdminResponse>) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [updateSurveyAssignment] = useUpdateSurveyAssignmentMutation();
  const { downloadSurveyView, isDownloading } = useSurveyViewDownload();
  const [filters, setFilters] =
    useState<Partial<SurveyStatisticsFiltersState>>(defaultFilters);

  const queryFilters = useMemo(() => {
    const { submissionTiming, ...rest } = filters;
    const timing: { submittedBefore?: string; submittedAfter?: string } = {};
    if (
      submissionTiming === SUBMISSION_TIMING.BEFORE_SOFT_DEADLINE &&
      softDeadline
    ) {
      timing.submittedBefore = softDeadline;
    } else if (
      submissionTiming === SUBMISSION_TIMING.AFTER_SOFT_DEADLINE &&
      softDeadline
    ) {
      timing.submittedAfter = softDeadline;
    }
    return { ...rest, ...timing };
  }, [filters, softDeadline]);

  const handleChangeSubmissionRequired = (
    detailsId: number,
    newValue: boolean,
  ) => {
    updateSurveyAssignment({
      id: detailsId,
      data: { isSubmissionRequired: newValue },
    })
      .unwrap()
      .then(() =>
        notification.success({
          message: t("survey.labels.surveyDetailsUpdated"),
        }),
      );
  };

  const handleChangeExcellenceAward = (
    detailsId: number,
    newValue: boolean,
  ) => {
    updateSurveyAssignment({
      id: detailsId,
      data: { isExcellenceAwardWinner: newValue },
    })
      .unwrap()
      .then(() =>
        notification.success({
          message: t("survey.labels.excellenceAwardUpdated"),
        }),
      );
  };

  const columns: Record<
    string,
    ColumnType<SurveyAssignmentStatisticsAdminResponse>
  > = useMemo(() => {
    return {
      name: {
        title: t("survey.labels.name"),
        key: "name",
        dataIndex: ["organization_name"],
        sorter: true,
        width: 400,
        ellipsis: true,
        render: (_, record) => {
          let navigateTo;
          let name;
          if (defaultFilters?.survey) {
            if (record?.organization) {
              navigateTo = `/reports/${defaultFilters.survey}/overview?organization=${record.organization.id}`;
              name = record.organization.name;
            } else if (record.user) {
              navigateTo = `/reports/${defaultFilters.survey}/overview?user=${record.user.id}`;
              name = record.user.fullName;
            } else {
              return;
            }
          }

          return (
            <Link
              style={{ textDecoration: "underline" }}
              to={navigateTo || "#"}
            >
              {name}
            </Link>
          );
        },
      },
      isSurveyStarted: {
        title: t("survey.labels.isSurveyStarted"),
        dataIndex: "isSurveyStarted",
        key: "isSurveyStarted",
        render: (_, record) =>
          record.isSurveyStarted ? (
            <CheckCircleOutlined style={{ color: "green" }} />
          ) : (
            <CloseCircleOutlined style={{ color: "red" }} />
          ),
      },
      answeredQuestionsCount: {
        title: t("survey.labels.answeredQuestions"),
        dataIndex: "answeredQuestionsCount",
        key: "answeredQuestionsCount",
      },
      attachedEvidenceCount: {
        title: t("survey.labels.attachedEvidence"),
        dataIndex: "answeredBlocksCount",
        key: "answeredBlocksCount",
      },
      isSubmissionRequired: {
        title: t("survey.labels.isSubmissionRequired"),
        dataIndex: "isSubmissionRequired",
        key: "isSubmissionRequired",
        render: (_, record) => {
          const handleClick = () =>
            handleChangeSubmissionRequired(
              record.id,
              !record.isSubmissionRequired,
            );
          return record.isSubmissionRequired ? (
            <CheckCircleOutlined
              onClick={handleClick}
              style={{ color: "green" }}
            />
          ) : (
            <CloseCircleOutlined
              onClick={handleClick}
              style={{ color: "red" }}
            />
          );
        },
      },
      isSubmitted: {
        title: t("survey.labels.isSubmitted"),
        dataIndex: "isSubmitted",
        key: "isSubmitted",
        render: (_, record) =>
          record.isSubmitted ? (
            <CheckCircleOutlined style={{ color: "green" }} />
          ) : (
            <CloseCircleOutlined style={{ color: "red" }} />
          ),
      },
      submittedAt: {
        title: t("survey.labels.submittedAt"),
        dataIndex: "submitted_at",
        key: "submittedAt",
        sorter: true,
        render: (_, record) =>
          record.submittedAt
            ? DateTime.fromISO(record.submittedAt).toFormat("dd MMM yyyy HH:mm")
            : "-",
      },
      downloadReport: {
        title: t("survey.labels.downloadReport"),
        key: "report",
        render: (_, record) => {
          const websiteSurveyViewId = record.websiteSurveyViewId;
          if (!websiteSurveyViewId) return;

          return isDownloading(websiteSurveyViewId) ? (
            <BaseSpinner size="small" />
          ) : (
            <DownloadOutlined
              onClick={() =>
                downloadSurveyView(
                  websiteSurveyViewId,
                  `${record.organization}.pdf`,
                )
              }
              style={{ color: "grey", fontSize: "20px" }}
            />
          );
        },
      },
      actions: {
        title: t("survey.labels.actions"),
        key: "actions",
        render: (_, record) => (
          <RevertSubmissionActionsCell
            assignment={record}
            surveyStatus={surveyStatus}
          />
        ),
      },
      excellenceAward: {
        title: t("survey.labels.excellenceAward"),
        key: "excellenceAward",
        render: (_, record) => (
          <Checkbox
            checked={record.isExcellenceAwardWinner}
            onChange={(e) =>
              handleChangeExcellenceAward(record.id, e.target.checked)
            }
          />
        ),
      },
    };
  }, [surveyStatus]);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) =>
          columns[
            columnKey
          ] as ColumnType<SurveyAssignmentStatisticsAdminResponse>,
      ),
    [columns, columnNames],
  );

  return (
    <div className="table-container-layout">
      <SurveyStatisticsFilters
        filterNames={filterNames}
        setFilters={(data) => {
          setFilters(data);
          if (filtersCallback) filtersCallback(data);
        }}
        defaultFilters={defaultFilters}
      />
      <QueryTable<SurveyAssignmentStatisticsAdminResponse>
        query={useLazyGetAdminSurveyStatisticsQuery}
        filters={queryFilters}
        initialSorting={{
          field: "name",
          order: "ascend",
        }}
        columns={mappedColumns}
        {...props}
      />
    </div>
  );
};

export default SurveyStatisticsTable;
